import React, { useState, useEffect } from 'react';
import { doc, getDoc, collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { Trophy, Star, Calendar, Zap, Medal, Award, Users, Gift, ChevronRight, AlertCircle } from 'lucide-react';

// Coming Soon Overlay Component
const ComingSoonOverlay = () => (
  <div className="absolute inset-0 flex items-center justify-center z-10">
    <div className="absolute inset-0 bg-black/30 backdrop-blur-lg"></div>
    <div className="relative z-20 transform -rotate-12">
      <div className="bg-white/[0.05] border border-amber-500/30 rounded-md px-6 py-3 backdrop-blur-sm">
        <div className="flex items-center gap-2">
          <AlertCircle className="w-5 h-5 text-amber-400/70" />
          <span className="text-amber-400/80 font-mono font-bold text-lg uppercase tracking-wider">Coming Soon</span>
        </div>
        <p className="text-white/50 text-xs font-mono uppercase tracking-wider mt-1">Feature in development</p>
      </div>
    </div>
  </div>
);

const UserGamificationDashboard = ({ userId }) => {
  const [activeTab, setActiveTab] = useState('stats');
  const [userStats, setUserStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [achievements, setAchievements] = useState([]);
  const [showNotification, setShowNotification] = useState(false);
  const [notification, setNotification] = useState(null);

  useEffect(() => {
    const fetchUserStats = async () => {
      if (!userId) return;
      
      try {
        setLoading(true);
        const userStatsRef = doc(db, "userStats", userId);
        const userStatsDoc = await getDoc(userStatsRef);
        
        if (userStatsDoc.exists()) {
          const data = userStatsDoc.data();
          setUserStats(data);
          
          // Prepare achievements list
          const unlockedAchievements = data.achievements?.unlocked || {};
          const achievementsList = Object.entries(unlockedAchievements).map(([id, details]) => ({
            id,
            ...details,
            unlocked: true
          }));
          
          setAchievements(achievementsList);
          
          // Check if there are recent achievements to show notifications
          // This would normally check timestamps, but for demo we'll simulate
        //   if (achievementsList.length > 0 && Math.random() > 0.7) {
        //     const recentAchievement = achievementsList[achievementsList.length - 1];
        //     setNotification({
        //       type: 'achievement',
        //       title: 'Achievement Unlocked!',
        //       message: `You've earned "${recentAchievement.name}"`,
        //       points: recentAchievement.points
        //     });
        //     setShowNotification(true);
        //   }
        }
      } catch (error) {
        console.error("Error fetching user stats:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchUserStats();
  }, [userId]);

  // Format number with commas
  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };
  
  // Calculate total achievements
  const totalAchievements = achievements.length;
  
  const renderStatsTab = () => (
    <div className="space-y-4">
      {/* Rank Progress Card */}
      <div className="bg-white/[0.02] rounded-md border border-white/[0.05] p-4 backdrop-blur-sm">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-emerald-400/70" />
            <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Current Rank</h3>
          </div>
          <span className="text-emerald-400/80 font-mono font-bold text-xl">{userStats?.rank?.current || 'Rookie'}</span>
        </div>

        <div className="mb-1">
          <div className="flex justify-between text-xs font-mono mb-1">
            <span className="text-white/30 uppercase tracking-wider">Next Rank: {userStats?.rank?.next || 'Scout'}</span>
            <span className="text-emerald-400/80 font-mono">{userStats?.rank?.progress || 0}%</span>
          </div>
          <div className="w-full bg-white/[0.05] rounded h-2">
            <div
              className="h-2 rounded"
              style={{
                width: `${userStats?.rank?.progress || 0}%`,
                background: 'linear-gradient(90deg, rgba(52, 211, 153, 0.8), rgba(139, 92, 246, 0.8))'
              }}
            ></div>
          </div>
        </div>
      </div>
      
      {/* Points Summary */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-white/[0.02] rounded-md border border-white/[0.05] p-4 backdrop-blur-sm">
          <div className="flex flex-col">
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/30">Total Points</span>
            <span className="text-white/80 text-2xl font-mono font-bold">{formatNumber(userStats?.points?.total)}</span>
          </div>
        </div>

        <div className="bg-white/[0.02] rounded-md border border-white/[0.05] p-4 backdrop-blur-sm">
          <div className="flex flex-col">
            <span className="text-[10px] font-mono uppercase tracking-[0.3em] text-white/30">Daily Points</span>
            <span className="text-white/80 text-2xl font-mono font-bold">{formatNumber(userStats?.points?.daily)}</span>
          </div>
        </div>
      </div>
      
      {/* Streak */}
      <div className="bg-white/[0.02] rounded-md border border-white/[0.05] p-4 backdrop-blur-sm">
        <div className="flex items-center gap-2 mb-2">
          <Calendar className="w-5 h-5 text-emerald-400/70" />
          <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Daily Streak</h3>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gradient-to-br from-emerald-500/10 to-violet-500/10 rounded-md flex items-center justify-center border border-white/[0.05]">
              <span className="text-emerald-400/80 font-mono font-bold text-xl">{userStats?.points?.streak || 0}</span>
            </div>
            <span className="text-white/50 font-mono text-sm uppercase tracking-wider">days</span>
          </div>

          <div className="text-white/30 text-[10px] font-mono uppercase tracking-wider">
            Keep your streak going for bonus points!
          </div>
        </div>
      </div>
      
      {/* Active Multipliers */}
      <div className="bg-white/[0.02] rounded-md border border-white/[0.05] p-4 backdrop-blur-sm">
        <div className="flex items-center gap-2 mb-2">
          <Zap className="w-5 h-5 text-violet-400/70" />
          <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Active Multipliers</h3>
        </div>

        <div className="flex flex-wrap gap-2">
          {userStats?.multipliers?.weekend > 0 && (
            <span className="bg-violet-500/10 border border-violet-500/20 text-violet-400/80 px-3 py-1 rounded text-xs font-mono uppercase tracking-wider">
              Weekend +25%
            </span>
          )}
          
          {userStats?.multipliers?.nightowl > 0 && (
            <span className="bg-violet-500/10 border border-violet-500/20 text-violet-400/80 px-3 py-1 rounded text-xs font-mono uppercase tracking-wider">
              Night Owl +50%
            </span>
          )}

          {userStats?.multipliers?.peakHours > 0 && (
            <span className="bg-amber-500/10 border border-amber-500/20 text-amber-400/80 px-3 py-1 rounded text-xs font-mono uppercase tracking-wider">
              Peak Hours +20%
            </span>
          )}
          
          {(!userStats?.multipliers?.weekend &&
           !userStats?.multipliers?.nightowl &&
           !userStats?.multipliers?.peakHours) && (
            <span className="text-white/30 text-xs font-mono uppercase tracking-wider">
              No active multipliers
            </span>
          )}
        </div>
      </div>
    </div>
  );
  
  const renderAchievementsTab = () => (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Your Achievements</h3>
        <span className="text-emerald-400/80 font-mono text-sm">{totalAchievements} unlocked</span>
      </div>
      
      {achievements.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {achievements.map(achievement => (
            <div
              key={achievement.id}
              className="bg-white/[0.02] rounded-md border border-white/[0.05] p-4 flex items-start gap-3 backdrop-blur-sm hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200"
            >
              <div className="mt-1 bg-gradient-to-br from-emerald-500/10 to-violet-500/10 p-2 rounded-md border border-white/[0.05]">
                <Award className="w-5 h-5 text-emerald-400/70" />
              </div>

              <div className="flex-1">
                <h4 className="text-white/80 font-mono text-sm">{achievement.name}</h4>
                <p className="text-white/50 text-xs font-mono mb-2">{achievement.description}</p>
                <div className="flex items-center gap-2">
                  <Star className="w-4 h-4 text-amber-400/70" />
                  <span className="text-amber-400/70 text-xs font-mono">+{achievement.points} points</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white/[0.02] rounded-md border border-white/[0.05] p-8 text-center backdrop-blur-sm">
          <Award className="w-10 h-10 text-white/20 mx-auto mb-3" />
          <p className="text-white/50 font-mono text-sm">No achievements unlocked yet.</p>
          <p className="text-white/30 text-xs font-mono uppercase tracking-wider mt-1">Keep interacting to unlock achievements!</p>
        </div>
      )}
    </div>
  );
  
  const renderLeaderboardTab = () => (
    <div className="space-y-4 relative">
      {/* Coming Soon Overlay */}
      <ComingSoonOverlay />
      
      {/* Content is still visible but blurred behind the overlay */}
      <h3 className="text-lg font-semibold text-white mb-4">Top Point Earners</h3>
      
      <div className="bg-slate-800/50 rounded-lg border border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="w-5 h-5 text-amber-400" />
            <span className="text-white font-medium">Weekly Leaderboard</span>
          </div>
          
          <span className="text-white/50 text-sm">Updated daily</span>
        </div>
        
        <div className="divide-y divide-white/10">
          {/* Demo leaderboard entries */}
          {[
            { rank: 1, name: "BetWizard", points: 4250, isUser: false },
            { rank: 2, name: "SportsGuru", points: 3890, isUser: false },
            { rank: 3, name: "You", points: userStats?.points?.total || 3074, isUser: true },
            { rank: 4, name: "PicksMaster", points: 2950, isUser: false },
            { rank: 5, name: "OddsChaser", points: 2720, isUser: false }
          ].map(entry => (
            <div 
              key={entry.rank}
              className={`p-4 flex items-center justify-between ${
                entry.isUser ? 'bg-emerald-500/10' : ''
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded flex items-center justify-center font-mono text-xs ${
                  entry.rank === 1 ? 'bg-amber-500/10 border border-amber-500/20 text-amber-400/80' :
                  entry.rank === 2 ? 'bg-white/[0.05] border border-white/[0.08] text-white/70' :
                  entry.rank === 3 ? 'bg-orange-700/10 border border-orange-700/20 text-orange-400/70' :
                  'bg-white/[0.02] border border-white/[0.05] text-white/50'
                }`}>
                  {entry.rank}
                </div>

                <span className={`font-mono text-sm ${
                  entry.isUser ? 'text-emerald-400/80' : 'text-white/70'
                }`}>
                  {entry.name}
                </span>
              </div>

              <div className="text-white/50 font-mono text-sm">{formatNumber(entry.points)}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  
  const renderRewardsTab = () => (
    <div className="space-y-4 relative">
      {/* Coming Soon Overlay */}
      <ComingSoonOverlay />
      
      {/* Content is still visible but blurred behind the overlay */}
      <div className="flex justify-between items-center mb-2">
        <h3 className="text-xs font-mono uppercase tracking-[0.2em] text-white/50">Rewards Shop</h3>
        <div className="text-emerald-400/80 font-mono text-sm">
          {formatNumber(userStats?.points?.total || 0)} points available
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          { 
            id: 'premium-week', 
            name: 'Premium Access (1 Week)', 
            description: 'Unlock premium features for one week', 
            cost: 1000,
            icon: Star
          },
          { 
            id: 'priority-analysis', 
            name: 'Priority Analysis', 
            description: 'Get in-depth analysis on a game of your choice', 
            cost: 750,
            icon: Zap
          },
          { 
            id: 'hot-tip', 
            name: 'Hot Tip Access', 
            description: 'Exclusive betting insights from our experts', 
            cost: 500,
            icon: Gift
          },
          { 
            id: 'custom-badge', 
            name: 'Custom Profile Badge', 
            description: 'Stand out with a unique profile badge', 
            cost: 2000,
            icon: Award
          }
        ].map(reward => (
          <div
            key={reward.id}
            className="bg-white/[0.02] rounded-md border border-white/[0.05] p-4 backdrop-blur-sm hover:bg-white/[0.03] hover:border-white/[0.08] transition-all duration-200"
          >
            <div className="flex items-start gap-3">
              <div className="mt-1 p-2 rounded-md bg-gradient-to-br from-emerald-500/10 to-violet-500/10 border border-white/[0.05]">
                {React.createElement(reward.icon, { className: "w-5 h-5 text-emerald-400/70" })}
              </div>

              <div className="flex-1">
                <h4 className="text-white/80 font-mono text-sm">{reward.name}</h4>
                <p className="text-white/50 text-xs font-mono mb-3">{reward.description}</p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-1">
                    <Star className="w-4 h-4 text-amber-400/70" />
                    <span className="text-amber-400/70 font-mono text-xs">
                      {formatNumber(reward.cost)} points
                    </span>
                  </div>

                  <button
                    className={`px-3 py-1 rounded text-xs font-mono uppercase tracking-wider transition-all duration-200 ${
                      (userStats?.points?.total || 0) >= reward.cost
                        ? 'bg-gradient-to-r from-emerald-500/20 to-violet-500/20 text-emerald-400/80 hover:from-emerald-500/30 hover:to-violet-500/30 border border-emerald-500/20'
                        : 'bg-white/[0.02] text-white/30 cursor-not-allowed border border-white/[0.05]'
                    }`}
                  >
                    Redeem
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
  
  const renderActiveTab = () => {
    switch (activeTab) {
      case 'achievements':
        return renderAchievementsTab();
      case 'leaderboard':
        return renderLeaderboardTab();
      case 'rewards':
        return renderRewardsTab();
      case 'stats':
      default:
        return renderStatsTab();
    }
  };
  
  // Achievement notification toast
  const renderNotification = () => {
    if (!showNotification || !notification) return null;
    
    return (
      <div className="fixed bottom-4 right-4 z-50 max-w-sm">
        <div className="bg-black/90 border border-emerald-500/30 rounded-md backdrop-blur-lg p-4 animate-fadeIn">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-gradient-to-br from-emerald-500/10 to-violet-500/10 rounded-md border border-white/[0.05]">
              <Award className="w-5 h-5 text-emerald-400/70" />
            </div>

            <div className="flex-1">
              <h4 className="text-emerald-400/80 font-mono text-sm uppercase tracking-wider">{notification.title}</h4>
              <p className="text-white/50 text-xs font-mono mt-1">{notification.message}</p>

              {notification.points && (
                <div className="flex items-center gap-2 mt-2">
                  <Star className="w-4 h-4 text-amber-400/70" />
                  <span className="text-amber-400/70 text-xs font-mono">+{notification.points} points</span>
                </div>
              )}
            </div>

            <button
              onClick={() => setShowNotification(false)}
              className="text-white/30 hover:text-white/50 p-1 transition-all duration-200"
            >
              ✕
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  if (loading) {
    return (
      <div className="p-8 text-center">
        <div className="animate-pulse flex justify-center mb-2">
          <Trophy className="w-8 h-8 text-emerald-400/50" />
        </div>
        <p className="text-white/30 font-mono text-xs uppercase tracking-wider">Loading stats...</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white/[0.02] rounded-md border border-white/[0.05] overflow-hidden backdrop-blur-sm">
      {/* Tab Navigation */}
      <div className="flex border-b border-white/[0.05]">
        {[
          { id: 'stats', label: 'Stats', icon: Trophy },
          { id: 'achievements', label: 'Achievements', icon: Award },
          { id: 'leaderboard', label: 'Leaderboard', icon: Users },
          { id: 'rewards', label: 'Rewards', icon: Gift }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 py-3 px-2 text-xs font-mono uppercase tracking-wider flex items-center justify-center gap-1.5 transition-all duration-200 ${
              activeTab === tab.id
                ? 'text-emerald-400/80 border-b-2 border-emerald-400/60 bg-gradient-to-t from-emerald-500/[0.05] to-transparent'
                : 'text-white/30 hover:text-white/50 hover:bg-white/[0.02]'
            }`}
          >
            {React.createElement(tab.icon, { className: "w-4 h-4" })}
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>
      
      {/* Tab Content */}
      <div className="p-4">
        {renderActiveTab()}
      </div>
      
      {/* Achievement notification */}
      {renderNotification()}
    </div>
  );
};

export default UserGamificationDashboard;